import React, { useEffect, useRef } from 'react';
import { Container, Button, Icon, Loader, Segment, Message, Tab, Header } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import InfiniteScroll from 'react-infinite-scroller';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { FeedFilters } from './FeedFilters';
import FeedCard from './FeedCard';
import { UrgencyFilters } from './UrgencyFilters';
import { UrgencyCard } from './UrgencyCard';
import { NewsCard } from './NewsCard';
import { InfluenceWidget } from './InfluenceWidget';
import { CreatePostModal } from './CreatePostModal';
import { sportOptions } from '../../app/common/options/sportOptions';

export default observer(function PlaygroundPage() {
  const { playgroundStore, userStore, profileStore } = useStore();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = userStore.user;

  // ── Deep-link state ───────────────────────────────────────────────────────
  const highlightPostId = searchParams.get('highlightPost');
  // ref so we only do the deep-link scroll once per page load
  const deepLinkHandled = useRef(false);
  // map postId → highlighted state (true = yellow flash)
  const [highlightedPostId, setHighlightedPostId] = React.useState<string | null>(null);

  const urgencyInitialised = useRef(false);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    playgroundStore.loadFeed(true);
    if (user?.userID && !profileStore.profile) {
      profileStore.loadProfile(user.userID);
    }
  }, []);

  // ── Deep-link handler ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!highlightPostId || deepLinkHandled.current) return;
    if (playgroundStore.loadingFeed) return; // wait for initial load to settle

    deepLinkHandled.current = true;
    handleDeepLink(highlightPostId);
  }, [highlightPostId, playgroundStore.loadingFeed]);

  const handleDeepLink = async (postId: string) => {
    try {
      // Ask the store to load enough pages so the post is present in feedItems.
      // loadFeedToPostAsync handles the page-number lookup + sequential loadFeed calls.
      const pageNumber = await playgroundStore.loadFeedToPostAsync(postId);
      if (pageNumber == null) return; // post not found — silently ignore

      // Highlight the post
      setHighlightedPostId(postId);

      // Allow React to render the newly-loaded items before scrolling
      await new Promise(r => setTimeout(r, 150));

      const el = document.getElementById(`feed-post-${postId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Remove highlight after 3 s
      setTimeout(() => setHighlightedPostId(null), 3000);

      // Clean the URL (remove ?highlightPost= without navigation)
      setSearchParams(prev => {
        prev.delete('highlightPost');
        return prev;
      }, { replace: true });

    } catch (err) {
      console.warn('Deep-link to feed post failed:', err);
    }
  };

  // ── Tab change ────────────────────────────────────────────────────────────
  const handleTabChange = async (_: any, data: any) => {
    const idx = data.activeIndex as number;
    const tabs: ('feed' | 'urgency' | 'news')[] = ['feed', 'urgency', 'news'];
    const tab = tabs[idx];
    playgroundStore.setActiveTab(tab);

    if (tab === 'urgency') {
      if (!urgencyInitialised.current) {
        urgencyInitialised.current = true;
        let profile = profileStore.profile;
        if (!profile && user?.userID) {
          await profileStore.loadProfile(user.userID);
          profile = profileStore.profile;
        }
        const favSportIds = profile?.sports?.map((s: any) => s.id) ?? [];
        if (favSportIds.length > 0) {
          playgroundStore.setUrgencyParams({ sportIds: favSportIds });
        }
      }
      playgroundStore.loadUrgency(true);
    }

    if (tab === 'news' && playgroundStore.newsItems.length === 0) {
      playgroundStore.loadNews();
    }
  };

  // ── Sport filters for news ────────────────────────────────────────────────
  const userSportFilters = (() => {
    const profile = profileStore.profile;
    if (!profile?.sports?.length) return [];
    return profile.sports
      .map((sport) => {
        const match = sportOptions.find(
          (o) => o.label.toLowerCase() === (sport.sportName ?? '').toLowerCase()
        );
        if (!match) return null;
        return {
          value: sport.sportName ?? match.label,
          label: t(`sports.${match.label.toLowerCase()}`, { defaultValue: match.label }),
        };
      })
      .filter(Boolean) as { value: string; label: string }[];
  })();

  // ── Feed tab ──────────────────────────────────────────────────────────────
  const FeedPane = (
    <div>
      <FeedFilters
        params={playgroundStore.feedParams}
        onParamChange={(p) => playgroundStore.setFeedParams(p)}
      />

      <Segment
        style={{
          borderRadius: 12,
          marginBottom: 14,
          padding: '10px 14px',
          cursor: 'pointer',
          background: '#f0f4ff',
          border: '1.5px solid #c7d7fd',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
        onClick={() => playgroundStore.setCreatePostModalOpen(true)}
      >
        <img
          src={user?.image || '/assets/user.png'}
          style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid #c7d7fd' }}
          alt='you'
        />
        <span style={{ color: '#6b82c4', flex: 1, fontSize: '0.9em' }}>
          {t('playground.whatsOnYourMind')}
        </span>
        <Button primary size='mini' style={{ borderRadius: 20 }}>
          <Icon name='pencil' /> {t('playground.post')}
        </Button>
      </Segment>

      {playgroundStore.loadingFeed && playgroundStore.feedItems.length === 0 ? (
        <Loader active inline='centered' style={{ marginTop: 40 }} />
      ) : playgroundStore.feedItems.length === 0 ? (
        <Message icon info style={{ borderRadius: 12 }}>
          <Icon name='inbox' />
          <Message.Content>
            <Message.Header>{t('playground.emptyFeed')}</Message.Header>
            {t('playground.emptyFeedHint')}
          </Message.Content>
        </Message>
      ) : (
        <InfiniteScroll
          pageStart={0}
          loadMore={playgroundStore.loadMoreFeed}
          hasMore={playgroundStore.hasMoreFeed}
          loader={<Loader key='loader' active inline='centered' style={{ margin: '20px auto' }} />}
        >
          {playgroundStore.feedItems.map(item => (
            <div
              key={`${item.id}-${item.type}`}
              id={`feed-post-${item.id}`}
              style={
                highlightedPostId === item.id
                  ? {
                      // Yellow flash highlight — fades after 3 s via CSS animation
                      borderRadius: 12,
                      outline: '3px solid #f59e0b',
                      outlineOffset: 2,
                      animation: 'feedHighlight 3s ease-out forwards',
                    }
                  : undefined
              }
            >
              <FeedCard item={item} currentUserId={user?.userID} />
            </div>
          ))}
        </InfiniteScroll>
      )}
    </div>
  );

  // ── Urgency tab ───────────────────────────────────────────────────────────
  const UrgencyPane = (
    <div>
      <UrgencyFilters
        params={playgroundStore.urgencyParams}
        onParamChange={(p) => {
          playgroundStore.setUrgencyParams(p);
          playgroundStore.loadUrgency(true);
        }}
      />
      <Message warning icon style={{ borderRadius: 12, marginBottom: 14 }}>
        <Icon name='lightning' />
        <Message.Content>
          {t('playground.urgency.hint', { hours: playgroundStore.urgencyParams.timeWindowHours })}
        </Message.Content>
      </Message>
      {playgroundStore.loadingUrgency && playgroundStore.urgencyItems.length === 0 ? (
        <Loader active inline='centered' style={{ marginTop: 40 }} />
      ) : playgroundStore.urgencyItems.length === 0 ? (
        <Message icon info style={{ borderRadius: 12 }}>
          <Icon name='search' />
          <Message.Content>
            <Message.Header>{t('playground.urgency.noGames')}</Message.Header>
            {t('playground.urgency.noGamesHint')}
          </Message.Content>
        </Message>
      ) : (
        <InfiniteScroll
          pageStart={0}
          loadMore={playgroundStore.loadMoreUrgency}
          hasMore={playgroundStore.hasMoreUrgency}
          loader={<Loader key='loader' active inline='centered' style={{ margin: '20px auto' }} />}
        >
          {playgroundStore.urgencyItems.map(item => (
            <UrgencyCard key={item.id} item={item} />
          ))}
        </InfiniteScroll>
      )}
    </div>
  );

  // ── News tab ──────────────────────────────────────────────────────────────
  const NewsPane = (
    <div>
      <div
        className='sport-filter-scroll'
        style={{
          marginBottom: 14,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          paddingBottom: 2,
        }}
      >
        <Button.Group size='mini' style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
          <Button
            key='all'
            primary={playgroundStore.newsSportFilter === 'all'}
            active={playgroundStore.newsSportFilter === 'all'}
            onClick={() => playgroundStore.setNewsSportFilter('all')}
          >
            {t('playground.news.allSports')}
          </Button>
          {userSportFilters.map(s => (
            <Button
              key={s.value}
              primary={playgroundStore.newsSportFilter === s.value}
              active={playgroundStore.newsSportFilter === s.value}
              onClick={() => playgroundStore.setNewsSportFilter(s.value)}
            >
              {s.label}
            </Button>
          ))}
        </Button.Group>
      </div>

      {playgroundStore.loadingNews ? (
        <Loader active inline='centered' style={{ marginTop: 40 }} />
      ) : playgroundStore.newsItems.length === 0 ? (
        <Message icon info style={{ borderRadius: 12 }}>
          <Icon name='newspaper' />
          <Message.Content>
            <Message.Header>{t('playground.news.noNews')}</Message.Header>
            {t('playground.news.noNewsHint')}
          </Message.Content>
        </Message>
      ) : (
        <div>
          {playgroundStore.newsItems.map(n => <NewsCard key={n.newsId} item={n} />)}
        </div>
      )}
    </div>
  );

  const panes = [
    {
      menuItem: {
        key: 'feed',
        content: <span><Icon name='home' color='blue' />{t('playground.tab.feed')}</span>,
      },
      render: () => <Tab.Pane style={{ border: 'none', padding: '14px 0' }}>{FeedPane}</Tab.Pane>,
    },
    {
      menuItem: {
        key: 'urgency',
        content: <span><Icon name='lightning' color='yellow' />{t('playground.tab.urgency')}</span>,
      },
      render: () => <Tab.Pane style={{ border: 'none', padding: '14px 0' }}>{UrgencyPane}</Tab.Pane>,
    },
    {
      menuItem: {
        key: 'news',
        content: <span><Icon name='newspaper' color='teal' />{t('playground.tab.news')}</span>,
      },
      render: () => <Tab.Pane style={{ border: 'none', padding: '14px 0' }}>{NewsPane}</Tab.Pane>,
    },
  ];

  return (
    <>
      <Container style={{ marginBottom: 20, marginTop: 20 }}>
        <Segment color='blue' style={{ borderRadius: 12 }}>
          <Header as='h1' color='blue'>
            <Icon name='newspaper' color='blue' />
            <Header.Content>
              {t('playground.tab.feed')} - {t('playground.title')}
              <Header.Subheader>{t('playground.subtitle')}</Header.Subheader>
            </Header.Content>
          </Header>
        </Segment>
      </Container>

      <Container style={{ paddingBottom: 40 }}>
        {user?.userID && (
          <div className='playground-widget-mobile' style={{ marginBottom: 16 }}>
            <InfluenceWidget userId={user.userID} isOwnProfile={true} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tab
              panes={panes}
              onTabChange={handleTabChange}
              menu={{
                secondary: true,
                pointing: true,
                style: { borderBottom: '2px solid #e8eaf6', marginBottom: 0 },
              }}
            />
          </div>
          {user?.userID && (
            <div style={{ width: 280, flexShrink: 0, display: 'none' }} className='playground-sidebar'>
              <InfluenceWidget userId={user.userID} isOwnProfile={true} />
            </div>
          )}
        </div>
      </Container>

      <CreatePostModal />

      <style>{`
        @media (min-width: 992px) {
          .playground-sidebar { display: block !important; }
          .playground-widget-mobile { display: none !important; }
        }
        @media (max-width: 991px) {
          .playground-widget-mobile { display: block; }
        }
        @media (max-width: 600px) {
          .ui.secondary.menu .item {
            padding: 8px 10px !important;
            font-size: 0.82em !important;
          }
        }
        .sport-filter-scroll::-webkit-scrollbar { display: none; }

        /* Deep-link highlight animation */
        @keyframes feedHighlight {
          0%   { outline-color: #f59e0b; background-color: #fffbeb; }
          70%  { outline-color: #f59e0b; background-color: #fffbeb; }
          100% { outline-color: transparent; background-color: transparent; }
        }
      `}</style>
    </>
  );
});